import { useCallback, useEffect, useMemo, useState } from "react";
import { callFunction } from "../api/callFunction";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import { Cog6ToothIcon, EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import Api from "../api/api";
import { db } from "../firebase";
import { fetchUsersByDocIds } from "../Events/utils";
import { PrimaryButtonSmall, SecondaryButtonSmall } from "../components/Button";
import { showToast } from "../utils/showToast";
import PayoutSettingsModal from "../components/PayoutSettingsModal";
import PayoutOverrideForm, { describeOverride } from "../components/PayoutOverrideForm";
import Utility from "../utils/utility";
import { variantURL, fallbackToOriginal } from "../utils/imageVariant";

const formatUSD = Utility.formatUSD;

const formatDate = (ms) => {
  const d = Utility.convertDate(ms);
  return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
};

const STATUS_STYLES = {
  available: { label: "Available", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
  pending: { label: "Pending", cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" },
  processing: { label: "Processing", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  paid: { label: "Paid", cls: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" },
  failed: { label: "Failed", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
  unknown: { label: "Unknown", cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
};

const SummaryCard = ({ label, value, sub }) => (
  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
    <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{value}</p>
    {sub ? <p className="text-xs text-gray-400 dark:text-gray-500">{sub}</p> : null}
  </div>
);

const HostRow = ({
  host,
  busy,
  onPayout,
  onMarkPaid,
  dealOpen,
  onToggleDeal,
  globalCfg,
  override,
  onSaveOverride,
  onCancelDeal,
  savingOverride,
}) => {
  const [expanded, setExpanded] = useState(false);
  const canStripePayout = host.onboardingComplete && host.available.cents > 0;
  const canManualPay = host.available.cents > 0;
  const busyHere = busy === host.vendorID;
  const overrideOnly = !!host.overrideOnly;
  const dealLabel = dealOpen ? "Hide deal" : host.hasOverride ? "Edit deal" : "Set deal";

  // The single most relevant money action goes inline; everything else is tucked
  // into the "⋯" overflow menu so the row stays uncluttered (especially on mobile).
  const menuActions = [];
  if (!overrideOnly) {
    if (canStripePayout) {
      // Pay-via-Stripe is the inline primary, so offer the manual route here.
      menuActions.push({ label: "Mark paid (manual)", onClick: () => onMarkPaid(host) });
    }
    menuActions.push({ label: dealLabel, onClick: () => onToggleDeal(host) });
    menuActions.push({ label: expanded ? "Hide details" : "View details", onClick: () => setExpanded((v) => !v) });
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          {host.photoUrl ? (
            <img
              src={variantURL(host.photoUrl, 200)}
              onError={fallbackToOriginal(host.photoUrl)}
              alt={host.name}
              className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
            />
          ) : (
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400">
              {(host.name || "?").charAt(0).toUpperCase()}
            </span>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate font-medium text-gray-900 dark:text-gray-100">{host.name}</p>
              {!overrideOnly && !host.onboardingComplete && (
                <span className="inline-flex rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                  No Stripe
                </span>
              )}
              {host.hasOverride && (
                <span
                  className="inline-flex rounded-full bg-purple-100 dark:bg-purple-900/40 px-2 py-0.5 text-xs font-medium text-purple-700 dark:text-purple-300"
                  title={host.effectiveHostRate != null ? `Custom split: host keeps ${Math.round(host.effectiveHostRate * 1000) / 10}%` : "Custom payout deal"}
                >
                  Override
                </span>
              )}
              {/* Only claim "Auto" when the sweep can actually pay them: it skips
                  hosts without Stripe onboarding, and a badge promising automatic
                  payment is how their balance sits unpaid with nobody looking. */}
              {!overrideOnly && host.autoPayoutEnabled && host.onboardingComplete && (
                <span
                  className="inline-flex rounded-full bg-sky-100 dark:bg-sky-900/40 px-2 py-0.5 text-xs font-medium text-sky-700 dark:text-sky-300"
                  title="Matured funds are paid to this host automatically every day"
                >
                  Auto
                </span>
              )}
              {!overrideOnly && host.autoPayoutEnabled && !host.onboardingComplete && (
                <span
                  className="inline-flex rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300"
                  title="Auto-payout is on for this host, but the daily run skips them until Stripe onboarding is finished — pay them manually meanwhile"
                >
                  Auto (blocked)
                </span>
              )}
            </div>
            <p className="truncate text-xs text-gray-400 dark:text-gray-500">
              {overrideOnly && override ? describeOverride(override) : host.email || host.vendorID}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 sm:justify-end sm:gap-6">
          {overrideOnly ? (
            <p className="text-xs italic text-gray-400 dark:text-gray-500">No payout activity yet</p>
          ) : (
            <div className="flex items-center gap-4 text-left sm:gap-6 sm:text-right">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Available</p>
                <p className="font-semibold text-green-700 dark:text-green-300">{formatUSD(host.available.cents)}</p>
                <p className="text-[11px] text-gray-400 dark:text-gray-500">{host.available.eventCount} event(s)</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Pending</p>
                <p className="font-semibold text-gray-600 dark:text-gray-300">{formatUSD(host.pending.cents)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Paid out</p>
                <p className="font-semibold text-indigo-700 dark:text-indigo-300">{formatUSD(host.paid.cents)}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            {overrideOnly ? (
              <SecondaryButtonSmall onClick={() => onToggleDeal(host)}>
                {dealLabel}
              </SecondaryButtonSmall>
            ) : (
              <>
                {canStripePayout ? (
                  <PrimaryButtonSmall onClick={() => onPayout(host)} loading={busyHere} loadingLabel="Paying…">
                    Pay via Stripe
                  </PrimaryButtonSmall>
                ) : canManualPay ? (
                  <SecondaryButtonSmall onClick={() => onMarkPaid(host)} loading={busyHere}>
                    Mark paid (manual)
                  </SecondaryButtonSmall>
                ) : null}

                <Menu as="div" className="relative inline-block text-left">
                  <MenuButton className="inline-flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-700 p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary">
                    <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
                  </MenuButton>
                  <MenuItems className="absolute right-0 z-20 mt-2 w-48 origin-top-right rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-white/10 focus:outline-none">
                    {menuActions.map((a) => (
                      <MenuItem key={a.label}>
                        {({ focus }) => (
                          <button
                            onClick={a.onClick}
                            className={`${focus ? "bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-200"} block w-full px-4 py-2 text-left text-sm`}
                          >
                            {a.label}
                          </button>
                        )}
                      </MenuItem>
                    ))}
                  </MenuItems>
                </Menu>
              </>
            )}
          </div>
        </div>
      </div>

      {dealOpen && (
        <div className="border-t border-gray-100 dark:border-gray-800 px-4 pb-4">
          <PayoutOverrideForm
            key={host.vendorID}
            vendorID={host.vendorID}
            globalCfg={globalCfg}
            initialOverride={override}
            onSave={(ov) => onSaveOverride(host.vendorID, ov)}
            onCancel={onCancelDeal}
            saving={savingOverride}
          />
        </div>
      )}

      {expanded && !overrideOnly && (
        <div className="border-t border-gray-100 dark:border-gray-800 px-4 pb-4 pt-3">
          {/* Per-event breakdown */}
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Events</p>
          {host.breakdown.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">No completed events with revenue.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 dark:text-gray-500">
                  <th className="py-1 pr-3 font-medium">Event</th>
                  <th className="py-1 pr-3 font-medium">Date</th>
                  <th className="py-1 pr-3 text-right font-medium">Gross</th>
                  <th className="py-1 pr-3 text-right font-medium">Host share</th>
                  <th className="py-1 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {host.breakdown.map((ev) => (
                  <tr key={ev.id} className="border-t border-gray-50 dark:border-gray-800">
                    <td className="py-1.5 pr-3 text-gray-800 dark:text-gray-200">{ev.name}</td>
                    <td className="py-1.5 pr-3 text-gray-500 dark:text-gray-400">{formatDate(ev.dateMs)}</td>
                    <td className="py-1.5 pr-3 text-right text-gray-500 dark:text-gray-400">{formatUSD(ev.revenueCents)}</td>
                    <td className="py-1.5 pr-3 text-right text-gray-800 dark:text-gray-200">
                      {formatUSD(ev.hostCents)}
                      {ev.introApplied && (
                        <span
                          className="ml-1 inline-flex rounded-full bg-sky-100 dark:bg-sky-900/40 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:text-sky-300"
                          title={ev.appliedHostRate != null ? `Boosted rate ${Math.round(ev.appliedHostRate * 1000) / 10}% applied` : "Boosted intro rate applied"}
                        >
                          {ev.appliedHostRate != null ? `${Math.round(ev.appliedHostRate * 1000) / 10}%` : "boost"}
                        </span>
                      )}
                      {ev.guaranteeApplied && (
                        <span
                          className="ml-1 inline-flex rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300"
                          title={ev.topUpCents ? `Topped up ${formatUSD(ev.topUpCents)} to meet the minimum` : "Minimum payout applied"}
                        >
                          min
                        </span>
                      )}
                    </td>
                    <td className="py-1.5"><StatusBadge status={ev.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Payout history */}
          {host.payouts.length > 0 && (
            <>
              <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Payout history</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 dark:text-gray-500">
                    <th className="py-1 pr-3 font-medium">Date</th>
                    <th className="py-1 pr-3 text-right font-medium">Amount</th>
                    <th className="py-1 pr-3 font-medium">Method</th>
                    <th className="py-1 pr-3 font-medium">Status</th>
                    <th className="py-1 font-medium">Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {host.payouts.map((p) => (
                    <tr key={p.id} className="border-t border-gray-50 dark:border-gray-800">
                      <td className="py-1.5 pr-3 text-gray-500 dark:text-gray-400">{formatDate(p.paidAtMs || p.createdAtMs)}</td>
                      <td className="py-1.5 pr-3 text-right text-gray-800 dark:text-gray-200">{formatUSD(p.amountCents)}</td>
                      <td className="py-1.5 pr-3 text-gray-500 dark:text-gray-400 capitalize">
                        {p.method}
                        {/* Which batches the daily run paid, as opposed to the host
                            or an admin — the row's Auto badge only reflects the
                            setting right now, not what happened back then. */}
                        {p.initiatedBy === "auto" && (
                          <span className="ml-1.5 rounded bg-sky-100 dark:bg-sky-900/40 px-1.5 py-0.5 text-[10px] font-medium normal-case text-sky-700 dark:text-sky-300">
                            auto
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 pr-3"><StatusBadge status={p.status} /></td>
                      <td className="py-1.5 truncate text-xs text-gray-400 dark:text-gray-500">{p.transferId || p.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Payouts = () => {
  const [payoutData, setPayoutData] = useState(null);
  const [configData, setConfigData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // vendorID currently acting on (payout)

  const [settingsOpen, setSettingsOpen] = useState(false);

  // Per-host deal (override) editing — one open at a time, owned here.
  const [editingDealVendorID, setEditingDealVendorID] = useState(null);
  const [savingOverride, setSavingOverride] = useState(false);

  // "Add a deal" host picker (for hosts with no payout activity and no deal yet).
  const [addMode, setAddMode] = useState(false);
  const [allVendors, setAllVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [vendorSearch, setVendorSearch] = useState("");
  const [addSelectedVendor, setAddSelectedVendor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [payoutsResult, configResult] = await Promise.all([
        callFunction("adminListPayouts"),
        callFunction("adminGetPayoutConfig", {}),
      ]);
      setPayoutData(payoutsResult);
      setConfigData(configResult);
    } catch (err) {
      console.error("load payouts failed", err);
      showToast(err.message || "Failed to load payouts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const globalCfg = configData ? configData.global || configData.effectiveDefaults : null;
  const meta = {
    version: configData?.global?.version ?? null,
    updatedBy: configData?.global?.updatedBy ?? null,
    updatedAtMs: configData?.global?.updatedAtMs ?? null,
    isDefault: !configData?.global,
  };

  const overrideMap = useMemo(() => {
    const m = {};
    (configData?.vendorOverrides || []).forEach((v) => { m[v.vendorID] = v.override; });
    return m;
  }, [configData]);

  // The payout rows, plus synthetic rows for hosts that have a custom deal but
  // no payout activity yet — so every override stays visible and editable.
  const rows = useMemo(() => {
    const base = payoutData?.hosts || [];
    const present = new Set(base.map((h) => h.vendorID));
    const extra = (configData?.vendorOverrides || [])
      .filter((v) => !present.has(v.vendorID))
      .map((v) => ({
        vendorID: v.vendorID,
        name: v.name || v.vendorID,
        email: null,
        photoUrl: v.photoURL || null,
        onboardingComplete: false,
        hasOverride: true,
        available: { cents: 0, eventCount: 0 },
        pending: { cents: 0 },
        paid: { cents: 0 },
        breakdown: [],
        payouts: [],
        overrideOnly: true,
      }));
    return [...base, ...extra];
  }, [payoutData, configData]);

  const handlePayout = useCallback(
    async (host) => {
      if (!host.onboardingComplete) {
        showToast("Host hasn't finished Stripe onboarding. Use 'Mark paid (manual)' instead.");
        return;
      }
      if (host.available.cents <= 0) return;
      if (!window.confirm(`Send ${formatUSD(host.available.cents)} to ${host.name} via Stripe?`)) return;
      setBusy(host.vendorID);
      try {
        const result = await callFunction("adminPayoutHost", { vendorID: host.vendorID });
        showToast(`Paid ${formatUSD(result.amountCents)} to ${host.name}`);
        await load();
      } catch (err) {
        console.error("adminPayoutHost failed", err);
        showToast(err.message || "Payout failed");
      } finally {
        setBusy(null);
      }
    },
    [load]
  );

  const handleMarkPaid = useCallback(
    async (host) => {
      if (host.available.cents <= 0) return;
      const note = window.prompt(
        `Mark ${formatUSD(host.available.cents)} for ${host.name} as paid OUTSIDE Stripe (bank/cash).\nThis does NOT move money. Optional note:`,
        ""
      );
      if (note === null) return; // cancelled
      setBusy(host.vendorID);
      try {
        const result = await callFunction("adminMarkPayoutPaid", { vendorID: host.vendorID, note });
        showToast(`Marked ${formatUSD(result.amountCents)} paid for ${host.name}`);
        await load();
      } catch (err) {
        console.error("adminMarkPayoutPaid failed", err);
        showToast(err.message || "Failed to mark paid");
      } finally {
        setBusy(null);
      }
    },
    [load]
  );

  const handleToggleDeal = useCallback((host) => {
    setAddMode(false);
    setAddSelectedVendor(null);
    setEditingDealVendorID((cur) => (cur === host.vendorID ? null : host.vendorID));
  }, []);

  const handleSaveOverride = useCallback(
    async (vendorID, override) => {
      setSavingOverride(true);
      try {
        await callFunction("adminSetVendorPayoutOverride", { vendorID, override });
        showToast(override ? "Deal saved" : "Deal cleared");
        setEditingDealVendorID(null);
        setAddMode(false);
        setAddSelectedVendor(null);
        await load();
      } catch (err) {
        console.error("adminSetVendorPayoutOverride failed", err);
        showToast(err.message || "Failed to save deal");
      } finally {
        setSavingOverride(false);
      }
    },
    [load]
  );

  const handleOpenAddMode = useCallback(async () => {
    setAddMode(true);
    setAddSelectedVendor(null);
    setVendorSearch("");
    setEditingDealVendorID(null);
    if (allVendors.length === 0) {
      setLoadingVendors(true);
      try {
        // Only the hosts' OWN user docs, keyed by doc id — not the whole users
        // collection. Previously this pulled every member's phone/email/device
        // token into the browser just to label a host picker (GIS-184).
        const vendors = await Api.getCollection("vendors");
        const users = await fetchUsersByDocIds(db, vendors.map((v) => v.id));
        const userMap = Object.fromEntries(users.map((u) => [u.id, u]));
        setAllVendors(vendors.map((v) => {
          const u = userMap[v.id] || {};
          return { vendorID: v.id, name: u.name || v.name || null, photoURL: u.photoURL || v.photoURL || null };
        }));
      } catch (err) {
        showToast("Failed to load hosts");
      } finally {
        setLoadingVendors(false);
      }
    }
  }, [allVendors.length]);

  // Hosts already shown as rows (active or overridden) don't need the picker.
  const shownVendorIDs = new Set(rows.map((r) => r.vendorID));
  const filteredVendors = allVendors.filter(
    (v) =>
      !shownVendorIDs.has(v.vendorID) &&
      (!vendorSearch || (v.name || "").toLowerCase().includes(vendorSearch.toLowerCase()))
  );

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Host Payouts</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage every host's earnings. Pay matured balances via Stripe, mark them paid if you settled
            off-Stripe, or set a custom payout deal per host.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SecondaryButtonSmall onClick={() => setSettingsOpen(true)} className={!globalCfg ? "pointer-events-none opacity-40" : ""}>
            <span className="flex items-center gap-1.5">
              <Cog6ToothIcon className="h-4 w-4" />
              Settings
            </span>
          </SecondaryButtonSmall>
          <SecondaryButtonSmall onClick={load} loading={loading}>
            Refresh
          </SecondaryButtonSmall>
        </div>
      </div>

      {loading && !payoutData ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Loading payouts…</p>
      ) : (
        <>
          {payoutData && payoutData.hosts.length > 0 && (
            <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <SummaryCard label="Available now" value={formatUSD(payoutData.totals.availableCents)} />
              <SummaryCard label="Pending" value={formatUSD(payoutData.totals.pendingCents)} />
              <SummaryCard label="Paid out" value={formatUSD(payoutData.totals.paidCents)} />
              <SummaryCard label="Hosts" value={payoutData.totals.hostCount} />
            </div>
          )}

          {rows.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">No host payout activity yet.</p>
          ) : (
            <div className="space-y-3">
              {rows.map((host) => (
                <HostRow
                  key={host.vendorID}
                  host={host}
                  busy={busy}
                  onPayout={handlePayout}
                  onMarkPaid={handleMarkPaid}
                  dealOpen={editingDealVendorID === host.vendorID}
                  onToggleDeal={handleToggleDeal}
                  globalCfg={globalCfg}
                  override={overrideMap[host.vendorID] || null}
                  onSaveOverride={handleSaveOverride}
                  onCancelDeal={() => setEditingDealVendorID(null)}
                  savingOverride={savingOverride}
                />
              ))}
            </div>
          )}

          {/* Add a custom deal for a host who has no payout activity yet. */}
          <div className="mt-4">
            {!addMode ? (
              <button
                onClick={handleOpenAddMode}
                className="text-sm font-medium text-primary hover:underline"
                disabled={!globalCfg}
              >
                + Add a per-host payout deal
              </button>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Add a payout deal for a host</p>
                  <button
                    onClick={() => { setAddMode(false); setAddSelectedVendor(null); }}
                    className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    Cancel
                  </button>
                </div>
                {!addSelectedVendor ? (
                  <>
                    <input
                      type="text"
                      placeholder="Search hosts…"
                      value={vendorSearch}
                      onChange={(e) => setVendorSearch(e.target.value)}
                      className="mb-2 w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    {loadingVendors ? (
                      <p className="text-xs text-gray-400 dark:text-gray-500">Loading hosts…</p>
                    ) : filteredVendors.length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {vendorSearch ? "No matching hosts." : "Every host is already listed above."}
                      </p>
                    ) : (
                      <div className="max-h-48 space-y-1 overflow-y-auto">
                        {filteredVendors.map((v) => (
                          <button
                            key={v.vendorID}
                            onClick={() => setAddSelectedVendor(v)}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            {v.photoURL ? (
                              <img src={variantURL(v.photoURL, 200)} onError={fallbackToOriginal(v.photoURL)} alt={v.name} className="h-6 w-6 flex-shrink-0 rounded-full object-cover" />
                            ) : (
                              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
                                {(v.name || "?").charAt(0).toUpperCase()}
                              </span>
                            )}
                            <span className="text-sm text-gray-800 dark:text-gray-200">{v.name || v.vendorID}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="mb-3 flex items-center gap-2">
                      {addSelectedVendor.photoURL ? (
                        <img src={variantURL(addSelectedVendor.photoURL, 200)} onError={fallbackToOriginal(addSelectedVendor.photoURL)} alt={addSelectedVendor.name} className="h-7 w-7 rounded-full object-cover" />
                      ) : (
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
                          {(addSelectedVendor.name || "?").charAt(0).toUpperCase()}
                        </span>
                      )}
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{addSelectedVendor.name || addSelectedVendor.vendorID}</p>
                      <button onClick={() => setAddSelectedVendor(null)} className="ml-auto text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                        ← Back
                      </button>
                    </div>
                    <PayoutOverrideForm
                      key={addSelectedVendor.vendorID}
                      vendorID={addSelectedVendor.vendorID}
                      globalCfg={globalCfg}
                      initialOverride={null}
                      onSave={(override) => handleSaveOverride(addSelectedVendor.vendorID, override)}
                      onCancel={() => { setAddMode(false); setAddSelectedVendor(null); }}
                      saving={savingOverride}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {settingsOpen && globalCfg && (
        <PayoutSettingsModal
          globalCfg={globalCfg}
          meta={meta}
          history={configData?.history || []}
          onClose={() => setSettingsOpen(false)}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default Payouts;
